import React, { useEffect, useState } from 'react';
import { agentPost } from '../utils/functionAuth';
import { shareLink, shareWasCancelled } from '../utils/nativeShare';
import { PRODUCT_NAME, shareIsOpen } from '../shared/homeHistoryReport';
import { HOME_REPORT_AGENT_PLAN_MESSAGE } from '../shared/homeownerEntitlement';

const ENDPOINT = '/.netlify/functions/home-history-report';

export async function requestHomeHistoryReport(clientId) {
  return agentPost(ENDPOINT, { action: 'request', clientId });
}

export async function homeReportEntitlementMap() {
  try {
    const result = await agentPost(ENDPOINT, { action: 'agent-list' });
    const listed = result && result.ok && result.data && result.data.clients;
    const plans = {};
    if (!Array.isArray(listed)) return plans;
    listed.forEach((row) => {
      if (row && row.id) plans[row.id] = row.entitled === true;
    });
    return plans;
  } catch (error) {
    return {};
  }
}

function linkOpen(share) {
  return shareIsOpen({
    expires_at: share && share.expiresAt,
    revoked_at: share && share.revokedAt,
  }, new Date());
}

export default function HomeHistoryReportAgent({ colors, isMobile }) {
  const text = (colors && colors.text) || '#1a1a1a';
  const muted = (colors && colors.textSecondary) || '#666';
  const surface = (colors && colors.surface) || '#fff';
  const border = (colors && colors.border) || '#e6e6e6';
  const [clients, setClients] = useState([]);
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState('');

  useEffect(() => {
    let cancelled = false;
    agentPost(ENDPOINT, { action: 'agent-list' })
      .then((result) => {
        if (cancelled) return;
        if (!result || !result.ok) {
          setNotice((result && result.error) || 'Reports could not be loaded.');
          return;
        }
        setClients((result.data && result.data.clients) || []);
      })
      .catch(() => {
        if (!cancelled) setNotice('Reports could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function ask(clientId) {
    setBusyId(clientId);
    setNotice('');
    try {
      const result = await requestHomeHistoryReport(clientId);
      if (!result.ok) {
        setNotice(result.error || 'The request could not be sent.');
        return;
      }
      setNotice('Requested');
      setClients((current) => current.map((row) => (
        row.id === clientId
          ? Object.assign({}, row, { requestStatus: 'Requested', requestDismissed: false })
          : row
      )));
    } finally {
      setBusyId('');
    }
  }

  async function sendShare(url) {
    try {
      await shareLink(url, PRODUCT_NAME);
    } catch (error) {
      if (!shareWasCancelled(error)) setNotice('The link could not be shared.');
    }
  }

  return (
    <div data-testid="home-history-report-agent" style={{ color: text, minWidth: 0 }}>
      <h2 style={{
        fontSize: isMobile ? '1.3rem' : '1.5rem',
        marginTop: 0,
        marginBottom: '0.35rem',
        fontWeight: 300,
        letterSpacing: '0.04em',
        color: text,
      }}
      >
        {PRODUCT_NAME}
      </h2>
      <p>
        You can ask a homeowner you sponsor for their report. The homeowner writes it and chooses what is visible.
      </p>
      {notice ? <p role="status">{notice}</p> : null}
      {clients.map((client) => {
        const status = client.requestStatus === 'Requested'
          || client.requestStatus === 'Shared'
          || client.requestStatus === 'Expired'
          || client.requestStatus === 'Revoked'
          || client.requestStatus === 'Declined'
          ? client.requestStatus
          : '';
        const planInactive = client.entitled === false;
        const canAsk = !client.archived && !planInactive && status !== 'Shared' && (
          client.requestDismissed === true || status !== 'Requested'
        );
        return (
          <article
            key={client.id}
            style={{
              background: surface,
              border: `1px solid ${border}`,
              borderRadius: '0.75rem',
              padding: '1rem',
              marginBottom: '0.75rem',
            }}
          >
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem' }}>{client.name || 'Homeowner'}</h3>
            <p style={{ margin: '0 0 0.5rem', color: muted }}>
              {[client.city, client.state].filter(Boolean).join(', ')}
            </p>
            {status ? <p style={{ fontWeight: 650 }}>{status}</p> : null}
            {client.archived ? (
              <p>This home is archived and can only be viewed.</p>
            ) : planInactive && status !== 'Requested' && status !== 'Shared' ? (
              <p>{HOME_REPORT_AGENT_PLAN_MESSAGE}</p>
            ) : canAsk ? (
              <button
                type="button"
                disabled={busyId === client.id}
                onClick={() => ask(client.id)}
                style={{
                  background: '#59c4c4',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  minHeight: '44px',
                  padding: '0.7rem 1rem',
                  fontWeight: 650,
                  cursor: 'pointer',
                }}
              >
                Request report
              </button>
            ) : null}
            {(client.shares || []).filter(linkOpen).map((share) => (
              <div key={share.id} style={{ marginTop: '0.5rem' }}>
                {share.url ? (
                  <div style={{ overflowWrap: 'anywhere' }}>{share.url}</div>
                ) : null}
                {share.url ? (
                  <button type="button" onClick={() => sendShare(share.url)} style={{ minHeight: '44px', marginTop: '0.35rem' }}>
                    Share link
                  </button>
                ) : null}
              </div>
            ))}
          </article>
        );
      })}
    </div>
  );
}
